import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, CheckCircle2, Clock, Download, FileText, Play, RefreshCw,
  Database, Globe2, Save, Server, Shield, Terminal, Trash2, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';

const API_URL = API_BASE;

const statusClass = (ok) => (
  ok ? 'bg-green-500/10 text-green-700 border-green-500/30' : 'bg-red-500/10 text-red-700 border-red-500/30'
);

const StatusBadge = ({ ok, label }) => (
  <Badge variant="outline" className={statusClass(ok)}>
    {ok ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
    {label || (ok ? 'OK' : 'Blocked')}
  </Badge>
);

const Field = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <label className="space-y-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <Input value={value || ''} type={type} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
  </label>
);

const LogBlock = ({ lines = [] }) => (
  <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-3 text-xs overflow-x-auto min-h-[120px]">
    {(lines.length ? lines : ['No run selected.']).join('\n')}
  </pre>
);

const latestRunForMode = (activeRun, history, mode) => {
  if (activeRun?.mode === mode) return activeRun;
  return (history || []).find((run) => run.mode === mode);
};

const actionButtonClass = (status, disabled = false, danger = false) => {
  if (status === 'running') return 'animate-pulse border-emerald-400 bg-emerald-50 text-emerald-900 shadow-sm shadow-emerald-200 hover:bg-emerald-100';
  if (status === 'ok') return 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100';
  if (status === 'failed' || danger) return 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100';
  if (disabled) return 'border-slate-200 bg-slate-100 text-slate-400';
  return 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50';
};

const progressForStatus = (status, activeProgress = 0) => {
  if (status === 'running') return activeProgress;
  if (status === 'ok' || status === 'failed') return 100;
  return 0;
};

const ACTION_STEP_KEYS = {
  'Compile the frontend': 'compile-frontend',
  Connect: 'connect',
  'Refresh checks': 'checks',
  'Local preflight': 'local-preflight',
  'Server preflight': 'server-preflight',
  'Start / Run': 'server-start',
  'Execute deploy': 'execute',
};

const FULL_PIPELINE_STEPS = [
  { key: 'compile-frontend', name: 'Compile the frontend', path: '/api/help-center/deploy-pipeline/compile-frontend' },
  { key: 'connect', name: 'Connect', path: '/api/help-center/deploy-pipeline/connect' },
  { key: 'checks', name: 'Refresh checks', path: '/api/help-center/deploy-pipeline/checks' },
  { key: 'local-preflight', name: 'Local preflight', path: '/api/help-center/deploy-pipeline/local-preflight' },
  { key: 'server-preflight', name: 'Server preflight', path: '/api/help-center/deploy-pipeline/server-preflight' },
  { key: 'server-start', name: 'Start / Run', path: '/api/help-center/deploy-pipeline/server-start' },
];

const FULL_PIPELINE_STEP_KEYS = [...FULL_PIPELINE_STEPS.map((step) => step.key), 'execute'];

const historyRunClass = (status) => {
  if (status === 'ok') return 'border-green-200 bg-green-50/40';
  if (status === 'failed') return 'border-red-200 bg-red-50/50';
  if (status === 'running') return 'animate-pulse border-emerald-300 bg-emerald-50/50';
  return 'border-slate-200 bg-white';
};

const ActionButton = ({
  icon: Icon,
  label,
  status = 'idle',
  progress = 0,
  danger = false,
  disabled = false,
  onClick,
}) => {
  return (
    <Button
      className={`h-auto min-h-10 w-full justify-start px-3 py-2 ${actionButtonClass(status, disabled, danger)}`}
      variant="outline"
      onClick={onClick}
      disabled={disabled}
    >
      <div className="flex w-full items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="shrink-0">{label}</span>
        <div className="ml-auto flex min-w-[92px] items-center gap-2">
          <div className="h-2 flex-1 rounded-sm border border-current/50 bg-white/70">
            <div className="h-full rounded-sm bg-current transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="w-8 text-right text-xs font-semibold">{progress}%</span>
        </div>
      </div>
    </Button>
  );
};

const blankDnsSetup = () => ({
  name: 'EzERP.PRO root',
  provider: 'Namecheap',
  accountUser: 'Namecheap',
  domain: 'ezerp.pro',
  host: '@',
  recordType: 'A',
  credentialRef: 'secure-secret-store:namecheap/ezerp.pro/ddns-password',
  automationMode: 'namecheap-dynamic-dns',
  enabled: true,
});

const DB_MODE_OPTIONS = [
  { key: 'sem_db', label: 'Sem DB', tone: 'border-slate-300 bg-white text-slate-800' },
  { key: 'standard', label: 'Com DB', tone: 'border-amber-300 bg-amber-50 text-amber-800' },
  { key: 'full_db', label: 'FULL DB', tone: 'border-red-400 bg-red-50 text-red-800' },
];

const HelpCenterDeployPipeline = () => {
  const [state, setState] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [message, setMessage] = useState('');
  const [actionProgress, setActionProgress] = useState(0);
  const [stepStates, setStepStates] = useState({});
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [dnsSetupForm, setDnsSetupForm] = useState(blankDnsSetup());
  const [editingDnsSetupId, setEditingDnsSetupId] = useState('');

  const token = useMemo(() => localStorage.getItem('ezerpToken'), []);

  const request = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data.detail?.reason || data.detail?.detail || data.detail || data.message || `HTTP ${res.status}`;
      throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
    return data;
  }, [token]);

  const loadState = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const data = await request('/api/help-center/deploy-pipeline/state');
      setState(data);
      setConfig(data.config || {});
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  useEffect(() => {
    if (!action) {
      setActionProgress(0);
      return undefined;
    }
    setActionProgress(8);
    const timer = window.setInterval(() => {
      setActionProgress((current) => Math.min(95, current + (current < 70 ? 9 : 3)));
    }, 700);
    return () => window.clearInterval(timer);
  }, [action]);

  const markStep = (key, patch) => {
    setStepStates((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        ...patch,
      },
    }));
  };

  const saveConfig = async () => {
    setSaving(true);
    setMessage('');
    try {
      const data = await request('/api/help-center/deploy-pipeline/config', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      setState(data.state);
      setConfig(data.config || {});
      setMessage('Configuration saved.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (name, path, body = {}, options = {}) => {
    const { preserveAction = false, rethrow = false, suppressMessage = false } = options;
    const stepKey = ACTION_STEP_KEYS[name];
    if (!preserveAction) setAction(name);
    if (!suppressMessage) setMessage('');
    if (stepKey) markStep(stepKey, { status: 'running', progress: 8 });
    try {
      const data = await request(path, { method: 'POST', body: JSON.stringify(body) });
      if (data.run) setLastRun(data.run);
      if (data.state) {
        setState(data.state);
        setConfig(data.state.config || {});
      } else if (data.checks) {
        await loadState();
      }
      if (stepKey) {
        markStep(stepKey, {
          status: data.run?.status || (data.ok === false ? 'failed' : 'ok'),
          progress: 100,
        });
      }
      if (!suppressMessage) setMessage(data.blocked ? data.reason : `${name} completed.`);
      return data;
    } catch (error) {
      if (stepKey) markStep(stepKey, { status: 'failed', progress: 100 });
      if (!suppressMessage) setMessage(error.message);
      if (rethrow) throw error;
      return null;
    } finally {
      if (!preserveAction) setAction(null);
    }
  };

  const setDbMode = async (dbMode) => {
    const nextConfig = { ...config, dbMode };
    setConfig(nextConfig);
    setMessage('');
    try {
      const data = await request('/api/help-center/deploy-pipeline/config', {
        method: 'PUT',
        body: JSON.stringify(nextConfig),
      });
      setState(data.state);
      setConfig(data.config || {});
    } catch (error) {
      setMessage(error.message);
    }
  };


  const runPublicRoute = async (fix = false) => {
    setAction(fix ? 'Fix DNS / Public Route' : 'Check public route');
    setMessage('');
    try {
      const data = await request(`/api/help-center/deploy-pipeline/public-route/${fix ? 'fix' : 'check'}`, {
        method: 'POST',
        body: '{}',
      });
      setState((current) => ({ ...(current || {}), publicRoute: data }));
      setMessage(data.message || 'Public route checked.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setAction(null);
    }
  };

  const saveDnsSetup = async () => {
    setAction('DNS setup');
    setMessage('');
    try {
      const path = editingDnsSetupId
        ? `/api/help-center/deploy-pipeline/dns-setups/${encodeURIComponent(editingDnsSetupId)}`
        : '/api/help-center/deploy-pipeline/dns-setups';
      const data = await request(path, {
        method: editingDnsSetupId ? 'PUT' : 'POST',
        body: JSON.stringify(dnsSetupForm),
      });
      setState(data);
      setConfig(data.config || {});
      setDnsSetupForm(blankDnsSetup());
      setEditingDnsSetupId('');
      setMessage(editingDnsSetupId ? 'DNS setup updated.' : 'DNS setup added.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setAction(null);
    }
  };

  const deleteDnsSetup = async (setupId) => {
    setAction('Delete DNS setup');
    setMessage('');
    try {
      const data = await request(`/api/help-center/deploy-pipeline/dns-setups/${encodeURIComponent(setupId)}`, {
        method: 'DELETE',
      });
      setState(data);
      setConfig(data.config || {});
      setMessage('DNS setup removed from management.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setAction(null);
    }
  };

  const downloadReport = async (runId) => {
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/help-center/deploy-pipeline/runs/${runId}/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Report download failed: HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${runId}.md`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const checks = state?.checks?.items || [];
  const history = useMemo(() => state?.history || [], [state?.history]);
  const latestExecuteRun = latestRunForMode(null, history, 'execute');
  const latestExecuteOkAt = latestExecuteRun?.status === 'ok' ? latestExecuteRun.started_at : null;
  const dbMode = config.dbMode || 'sem_db';
  const selectedDbMode = DB_MODE_OPTIONS.find((item) => item.key === dbMode) || DB_MODE_OPTIONS[0];
  const selectedCount = selectedRunIds.length;
  const deletingHistory = action === 'Delete history';
  const allHistorySelected = history.length > 0 && selectedCount === history.length;
  const executeReady = Boolean(state?.checks?.execute_ready);
  const nextGate = state?.nextGate || state?.checks?.next_gate;
  const preflightsReady = Boolean(state?.localPreflightStatus?.ok && state?.serverPreflightStatus?.ok);
  const publicRoute = state?.publicRoute || {};
  const dnsSetups = config?.dnsSetups || state?.config?.dnsSetups || [];
  const serverStartRun = latestRunForMode(state?.activeRun, history, 'server-start');
  const actionRuns = {
    'compile-frontend': latestRunForMode(state?.activeRun, history, 'compile-frontend'),
    connect: latestRunForMode(state?.activeRun, history, 'connect'),
    'local-preflight': latestRunForMode(state?.activeRun, history, 'local-preflight'),
    'server-preflight': latestRunForMode(state?.activeRun, history, 'server-preflight'),
    execute: latestRunForMode(state?.activeRun, history, 'execute'),
  };
  const serverStartBusy = action === 'Start / Run' || serverStartRun?.status === 'running';
  const serverStartTone = serverStartBusy ? 'running' : serverStartRun?.status === 'ok' ? 'ok' : serverStartRun?.status === 'failed' ? 'failed' : 'idle';
  const serverStartLabel = serverStartBusy ? 'Starting...' : serverStartTone === 'ok' ? 'Running' : serverStartTone === 'failed' ? 'Needs attention' : 'Start / Run';
  const serverStartClass = {
    running: 'border-emerald-300 bg-emerald-100 text-emerald-800 shadow-emerald-200/50',
    ok: 'border-emerald-400 bg-emerald-500 text-white shadow-emerald-300/60',
    failed: 'border-red-300 bg-red-50 text-red-700 shadow-red-200/50',
    idle: 'border-slate-300 bg-white text-slate-800 shadow-slate-200/60',
  }[serverStartTone];
  const actionStatus = (run, actionName, fallback = 'idle') => {
    if (action === actionName) return 'running';
    if (run?.status === 'running') return 'running';
    if (run?.status === 'ok') return 'ok';
    if (run?.status === 'failed') return 'failed';
    return fallback;
  };
  const runIsSupersededByExecute = (run) => (
    Boolean(latestExecuteOkAt && run?.started_at && run.started_at < latestExecuteOkAt)
  );
  const stepStatus = (key, run, actionName, fallback = 'idle') => {
    if (action === actionName || stepStates[key]?.status === 'running') return 'running';
    if (stepStates[key]?.status) return stepStates[key].status;
    if (run?.status === 'failed' && runIsSupersededByExecute(run)) return fallback;
    return actionStatus(run, actionName, fallback);
  };
  const stepProgress = (key, status) => {
    if (status === 'running') return actionProgress;
    if (stepStates[key]?.progress != null) return stepStates[key].progress;
    return progressForStatus(status, actionProgress);
  };
  const isGreen = (status) => status === 'ok';
  const compileFrontendUiStatus = stepStatus('compile-frontend', actionRuns['compile-frontend'], 'Compile the frontend', state?.compileFrontendStatus?.ok ? 'ok' : 'idle');
  const connectUiStatus = stepStatus('connect', actionRuns.connect, 'Connect', state?.connectStatus?.ok ? 'ok' : 'idle');
  const checksUiStatus = stepStatus('checks', null, 'Refresh checks', state?.checks?.all_ok ? 'ok' : 'idle');
  const localPreflightUiStatus = stepStatus('local-preflight', actionRuns['local-preflight'], 'Local preflight', state?.localPreflightStatus?.ok ? 'ok' : 'idle');
  const serverPreflightUiStatus = stepStatus('server-preflight', actionRuns['server-preflight'], 'Server preflight', state?.serverPreflightStatus?.ok ? 'ok' : 'idle');
  const serverStartUiStatus = stepStatus('server-start', serverStartRun, 'Start / Run');
  const executeUiStatus = stepStatus('execute', actionRuns.execute, 'Execute deploy');
  const compileFrontendUnlocked = true;
  const connectUnlocked = isGreen(compileFrontendUiStatus);
  const checksUnlocked = connectUnlocked && isGreen(connectUiStatus);
  const localPreflightUnlocked = checksUnlocked && isGreen(checksUiStatus);
  const serverPreflightUnlocked = localPreflightUnlocked && isGreen(localPreflightUiStatus);
  const serverStartUnlocked = serverPreflightUnlocked && isGreen(serverPreflightUiStatus);
  const executeUnlocked = serverStartUnlocked && isGreen(serverStartUiStatus);
  const allPreviousStepsGreen = executeUnlocked;

  useEffect(() => {
    const available = new Set(history.map((run) => run.run_id));
    setSelectedRunIds((current) => current.filter((runId) => available.has(runId)));
  }, [history]);

  const toggleRunSelection = (runId, checked) => {
    setSelectedRunIds((current) => {
      if (checked) return Array.from(new Set([...current, runId]));
      return current.filter((item) => item !== runId);
    });
  };

  const toggleAllHistory = (checked) => {
    setSelectedRunIds(checked ? history.map((run) => run.run_id) : []);
  };

  const deleteSelectedHistory = async () => {
    if (!selectedRunIds.length) return;
    setAction('Delete history');
    setMessage('');
    try {
      const data = await request('/api/help-center/deploy-pipeline/history/delete', {
        method: 'POST',
        body: JSON.stringify({ runIds: selectedRunIds }),
      });
      setSelectedRunIds([]);
      setState((current) => ({ ...(current || {}), history: data.history || [] }));
      setMessage(`Deleted ${data.deleted || 0} deploy history ${data.deleted === 1 ? 'run' : 'runs'}.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setAction(null);
    }
  };

  const executeDeploy = async () => {
    if (!allPreviousStepsGreen) {
      setMessage('Execute Deploy is locked until Connect, Refresh Checks, Local Preflight, Server Preflight, and Start / Run are green.');
      return;
    }
    if (dbMode !== 'full_db') {
      await runAction('Execute deploy', '/api/help-center/deploy-pipeline/execute');
      return;
    }
    const confirmation = window.prompt('Type FULL DB to replace the server database with the local database.');
    if (String(confirmation || '').trim().toUpperCase() !== 'FULL DB') {
      setMessage('FULL DB deploy cancelled. Typed confirmation is required.');
      return;
    }
    await runAction('Execute deploy', '/api/help-center/deploy-pipeline/execute', { fullDbConfirm: confirmation || '' });
  };

  const runFullPipeline = async () => {
    if (action) return;
    let fullDbConfirm = '';
    if (dbMode === 'full_db') {
      const confirmation = window.prompt('Type FULL DB to run the full deploy pipeline and replace the server database with the local database.');
      if (String(confirmation || '').trim().toUpperCase() !== 'FULL DB') {
        setMessage('FULL DB deploy pipeline cancelled. Typed confirmation is required.');
        return;
      }
      fullDbConfirm = confirmation || '';
    }

    setAction('Run Full Deploy Pipeline');
    setMessage('Running full deploy pipeline.');
    setStepStates(Object.fromEntries(FULL_PIPELINE_STEP_KEYS.map((key) => [key, { status: 'idle', progress: 0 }])));
    let currentStepKey = '';
    try {
      for (const step of FULL_PIPELINE_STEPS) {
        currentStepKey = step.key;
        await runAction(step.name, step.path, {}, {
          preserveAction: true,
          rethrow: true,
          suppressMessage: true,
        });
      }
      currentStepKey = 'execute';
      await runAction(
        'Execute deploy',
        '/api/help-center/deploy-pipeline/execute',
        dbMode === 'full_db' ? { fullDbConfirm } : {},
        {
          preserveAction: true,
          rethrow: true,
          suppressMessage: true,
        },
      );
      await loadState();
      setMessage('Full deploy pipeline completed.');
    } catch (error) {
      setStepStates((current) => {
        const failedIndex = FULL_PIPELINE_STEP_KEYS.indexOf(currentStepKey);
        const next = { ...current };
        FULL_PIPELINE_STEP_KEYS.forEach((key, index) => {
          if (failedIndex >= 0 && index > failedIndex) next[key] = { status: 'idle', progress: 0 };
        });
        return next;
      });
      setMessage(`Full deploy pipeline stopped: ${error.message}`);
    } finally {
      setAction(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              EzERP Deploy Pipeline
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Backup-first release workflow with checks, preflight, reports, and rollback readiness.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge ok={state?.hostSupport?.supported} label={state?.hostSupport?.supported ? 'Host Ready' : 'Host Blocked'} />
            <button
              type="button"
              onClick={() => runAction('Start / Run', '/api/help-center/deploy-pipeline/server-start')}
              disabled={!!action}
              className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-full border text-sm font-semibold shadow-lg transition hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 ${serverStartClass}`}
              title="Restart EzERP backend and nginx, then verify server health"
            >
              <span className="text-center leading-tight">{serverStartLabel}</span>
            </button>
            <Button variant="outline" size="sm" onClick={loadState} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600" />
              <span>{message}</span>
            </div>
          )}
          {!executeReady && nextGate && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600" />
              <span>Next gate: {nextGate.label}. {nextGate.detail}</span>
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {(state?.process || []).map((step) => (
              <div key={step.id} className="rounded-lg border p-3">
                <div className="font-medium text-sm">{step.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{step.description}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Database className="h-4 w-4" />
              Database deploy mode
            </div>
            <div className="flex flex-wrap gap-2">
              {DB_MODE_OPTIONS.map((mode) => {
                const active = dbMode === mode.key;
                return (
                  <button
                    key={mode.key}
                    type="button"
                    onClick={() => setDbMode(mode.key)}
                    disabled={!!action}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${active ? mode.tone : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <div className={`mt-2 inline-flex rounded-md border px-2 py-1 text-xs font-medium ${selectedDbMode.tone}`}>
              Selected: {selectedDbMode.label}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe2 className="h-4 w-4" />
              Public Route Doctor
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Separate from deploy: Namecheap DNS, WAN IP, ports 80/443, and public readiness.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge ok={publicRoute.status === 'running' || publicRoute.status === 'dns_fixed'} label={publicRoute.label || 'Route Warn'} />
            <Button variant="outline" size="sm" onClick={() => runPublicRoute(false)} disabled={!!action}>
              Check route
            </Button>
            <Button size="sm" onClick={() => runPublicRoute(true)} disabled={!!action}>
              Fix DNS / Public Route
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            {[
              ['Provider', publicRoute.dnsProvider || config.dnsProvider || 'Namecheap'],
              ['Domain', publicRoute.domain || config.dnsDomain || 'ezerp.pro'],
              ['Host', publicRoute.host || config.dnsHost || '@'],
              ['DNS IP', publicRoute.dnsIp || '-'],
              ['WAN IP', publicRoute.wanIp || '-'],
              ['DNS = WAN', publicRoute.dnsMatchesWan == null ? '-' : (publicRoute.dnsMatchesWan ? 'OK' : 'Mismatch')],
              ['TCP 80', publicRoute.tcp80 == null ? '-' : (publicRoute.tcp80 ? 'OK' : 'Blocked')],
              ['TCP 443', publicRoute.tcp443 == null ? '-' : (publicRoute.tcp443 ? 'OK' : 'Blocked')],
              ['Readiness', publicRoute.readinessOk == null ? '-' : (publicRoute.readinessOk ? 'OK' : 'Failed')],
              ['Credential', publicRoute.credentialConfigured == null ? (publicRoute.credentialRef || config.dnsCredentialRef || '-') : (publicRoute.credentialConfigured ? 'Configured' : 'Missing')],
              ['Last check', publicRoute.lastCheckedAt || publicRoute.lastUpdateAt || '-'],
              ['Automation', config.dnsAutomationMode || 'namecheap-dynamic-dns'],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="mt-1 break-all text-sm font-medium">{String(value)}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            {publicRoute.message || 'Run public route check to verify the external EzERP.PRO route.'}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border p-4 space-y-3">
              <div className="font-medium">{editingDnsSetupId ? 'Edit DNS setup' : 'Add DNS setup'}</div>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Name" value={dnsSetupForm.name} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, name: value })} />
                <Field label="Provider" value={dnsSetupForm.provider} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, provider: value })} />
                <Field label="Account reference" value={dnsSetupForm.accountUser} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, accountUser: value })} />
                <Field label="Domain" value={dnsSetupForm.domain} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, domain: value })} />
                <Field label="Host" value={dnsSetupForm.host} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, host: value })} />
                <Field label="Record type" value={dnsSetupForm.recordType} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, recordType: value })} />
                <Field label="Credential reference" value={dnsSetupForm.credentialRef} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, credentialRef: value })} />
                <Field label="Automation" value={dnsSetupForm.automationMode} onChange={(value) => setDnsSetupForm({ ...dnsSetupForm, automationMode: value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!dnsSetupForm.enabled} onChange={(event) => setDnsSetupForm({ ...dnsSetupForm, enabled: event.target.checked })} />
                Enabled for public route automation
              </label>
              <div className="flex gap-2">
                <Button onClick={saveDnsSetup} disabled={!!action}>{editingDnsSetupId ? 'Save edit' : 'Add setup'}</Button>
                <Button variant="outline" onClick={() => { setEditingDnsSetupId(''); setDnsSetupForm(blankDnsSetup()); }} disabled={!!action}>New setup</Button>
              </div>
            </div>
            <div className="rounded-lg border p-4 space-y-3">
              <div className="font-medium">Managed DNS setups</div>
              {dnsSetups.map((setup) => (
                <div key={setup.id} className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{setup.name || setup.domain}</div>
                    <div className="text-xs text-muted-foreground">{setup.provider} / {setup.domain} / {setup.host} / {setup.recordType}</div>
                    <div className="text-xs text-muted-foreground">{setup.credentialRef}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {setup.id === config.activeDnsSetupId && <StatusBadge ok label="Active" />}
                    <Button variant="outline" size="sm" onClick={() => { setEditingDnsSetupId(setup.id); setDnsSetupForm({ ...blankDnsSetup(), ...setup }); }} disabled={!!action}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => deleteDnsSetup(setup.id)} disabled={!!action}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-4 w-4" />
              Checks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {checks.map((item) => (
                <div key={item.key} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{item.label}</span>
                    <StatusBadge ok={item.ok} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">{item.detail}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Terminal className="h-4 w-4" />
              Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <ActionButton
              icon={Play}
              label={`Run Full Deploy Pipeline (${selectedDbMode.label})`}
              status={action === 'Run Full Deploy Pipeline' ? 'running' : 'idle'}
              progress={action === 'Run Full Deploy Pipeline' ? actionProgress : 0}
              danger={false}
              onClick={runFullPipeline}
              disabled={!!action}
            />
            <div className="border-t border-slate-200 pt-2" />
            <ActionButton
              icon={Terminal}
              label="Compile the frontend"
              status={compileFrontendUiStatus}
              progress={stepProgress('compile-frontend', compileFrontendUiStatus)}
              onClick={() => runAction('Compile the frontend', '/api/help-center/deploy-pipeline/compile-frontend')}
              disabled={!!action || !compileFrontendUnlocked}
            />
            <ActionButton
              icon={Server}
              label="Connect"
              status={connectUiStatus}
              progress={stepProgress('connect', connectUiStatus)}
              onClick={() => runAction('Connect', '/api/help-center/deploy-pipeline/connect')}
              disabled={!!action || !connectUnlocked}
            />
            <ActionButton
              icon={RefreshCw}
              label="Refresh Checks"
              status={checksUiStatus}
              progress={stepProgress('checks', checksUiStatus)}
              onClick={() => runAction('Refresh checks', '/api/help-center/deploy-pipeline/checks')}
              disabled={!!action || !checksUnlocked}
            />
            <ActionButton
              icon={Clock}
              label="Local Preflight"
              status={localPreflightUiStatus}
              progress={stepProgress('local-preflight', localPreflightUiStatus)}
              onClick={() => runAction('Local preflight', '/api/help-center/deploy-pipeline/local-preflight')}
              disabled={!!action || !localPreflightUnlocked}
            />
            <ActionButton
              icon={Server}
              label="Server Preflight"
              status={serverPreflightUiStatus}
              progress={stepProgress('server-preflight', serverPreflightUiStatus)}
              onClick={() => runAction('Server preflight', '/api/help-center/deploy-pipeline/server-preflight')}
              disabled={!!action || !serverPreflightUnlocked}
            />
            <ActionButton
              icon={Play}
              label="Start / Run"
              status={serverStartUiStatus}
              progress={stepProgress('server-start', serverStartUiStatus)}
              onClick={() => runAction('Start / Run', '/api/help-center/deploy-pipeline/server-start')}
              disabled={!!action || !serverStartUnlocked}
            />
            <ActionButton
              icon={Play}
              label={`Execute Deploy (${selectedDbMode.label})`}
              status={executeUiStatus}
              progress={stepProgress('execute', executeUiStatus)}
              danger={dbMode === 'full_db'}
              onClick={executeDeploy}
              disabled={!!action || !executeUnlocked}
            />
            {!executeReady && (
              <p className="text-xs text-muted-foreground">
                {nextGate ? `Follow the actions from top to bottom. Current gate: ${nextGate.label}.` : 'Run each action above until all are green before final deploy.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Save className="h-4 w-4" />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Field label="Mode" value={config.deploymentMode} onChange={(value) => setConfig({ ...config, deploymentMode: value })} />
            <Field label="Server host" value={config.serverHost} onChange={(value) => setConfig({ ...config, serverHost: value })} />
            <Field label="Server user" value={config.serverUser} onChange={(value) => setConfig({ ...config, serverUser: value })} />
            <Field label="SSH port" value={config.sshPort} onChange={(value) => setConfig({ ...config, sshPort: value })} />
            <Field label="SSH key path" value={config.sshKey} onChange={(value) => setConfig({ ...config, sshKey: value })} />
            <Field label="Remote incoming" value={config.remoteIncoming} onChange={(value) => setConfig({ ...config, remoteIncoming: value })} />
            <Field label="Health URL" value={config.healthUrl} onChange={(value) => setConfig({ ...config, healthUrl: value })} />
            <Field label="Database" value={config.databaseName} onChange={(value) => setConfig({ ...config, databaseName: value })} />
            <Field label="DB deploy mode" value={config.dbMode} onChange={(value) => setConfig({ ...config, dbMode: value })} />
            <Field label="Backend service" value={config.backendService} onChange={(value) => setConfig({ ...config, backendService: value })} />
            <Field label="Frontend webroot" value={config.frontendWebRoot} onChange={(value) => setConfig({ ...config, frontendWebRoot: value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={Boolean(config.allowExecute)} onChange={(event) => setConfig({ ...config, allowExecute: event.target.checked })} />
            Deploy execution armed after preflights pass
          </label>
          {preflightsReady && !config.allowExecute && (
            <p className="text-xs text-muted-foreground">
              Run Server Preflight again to arm Execute Deploy automatically.
            </p>
          )}
          <Button onClick={saveConfig} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            Save Configuration
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Canonical Commands
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LogBlock lines={Object.entries(state?.commands || {}).map(([key, value]) => `${key}: ${value}`)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Terminal className="h-4 w-4" />
              Last Run Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LogBlock lines={lastRun?.log || []} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="text-base">History</CardTitle>
            {selectedCount > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {selectedCount} selected
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {history.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={allHistorySelected}
                  onChange={(event) => toggleAllHistory(event.target.checked)}
                />
                Select all
              </label>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={deleteSelectedHistory}
              disabled={selectedCount === 0 || deletingHistory}
              className={
                deletingHistory
                  ? 'animate-pulse border-amber-300 bg-amber-50 text-amber-800'
                  : selectedCount > 0
                    ? 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100 hover:text-red-900'
                    : 'border-slate-200 bg-slate-100 text-slate-400'
              }
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {deletingHistory ? 'Deleting...' : `Delete${selectedCount ? ` (${selectedCount})` : ''}`}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {history.length === 0 && <div className="text-sm text-muted-foreground">No deploy pipeline runs yet.</div>}
            {history.map((run) => (
              <div key={run.run_id} className={`flex items-center justify-between gap-3 rounded-lg border p-3 text-sm ${historyRunClass(run.status)}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedRunIds.includes(run.run_id)}
                    onChange={(event) => toggleRunSelection(run.run_id, event.target.checked)}
                    aria-label={`Select ${run.release_id}`}
                  />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{run.release_id}</div>
                    <div className="text-xs text-muted-foreground">{run.mode} · {run.dbModeLabel || run.dbMode || 'Sem DB'} · {run.started_at}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge ok={run.status === 'ok'} label={run.status} />
                  <Button variant="outline" size="sm" onClick={() => downloadReport(run.run_id)}>
                    <Download className="h-4 w-4 mr-2" />
                    {run.status === 'failed' ? 'Log' : 'Report'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenterDeployPipeline;
